import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MagnifyingGlass, DownloadSimple, X, RocketLaunch, Tag, Books } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

export default function EquipmentCatalog() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [family, setFamily] = useState("transformers");
  const [q, setQ] = useState("");
  const [types, setTypes] = useState([]);
  const [sel, setSel] = useState(null);
  const [basis, setBasis] = useState("iec");
  const [facetFilter, setFacetFilter] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/equipment/meta").then(({ data }) => setMeta(data)).catch((e) => toast.error(fmtErr(e)));
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/equipment/types", { params: { family } })
      .then(({ data }) => setTypes(data))
      .catch((e) => toast.error(fmtErr(e)))
      .finally(() => setLoading(false));
    setFacetFilter({});
    setSel(null);
  }, [family]);

  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const active = Object.entries(facetFilter).filter(([, v]) => v);
    const rows = types.filter((t) => {
      if (needle && !`${t.name} ${t.description} ${(t.applications || []).join(" ")} ${t.category}`.toLowerCase().includes(needle)) return false;
      return active.every(([g, v]) => (t.facets?.[g] || []).includes(v));
    });
    const byCat = {};
    rows.forEach((t) => { (byCat[t.category] = byCat[t.category] || []).push(t); });
    return Object.entries(byCat);
  }, [types, q, facetFilter]);

  const shown = groups.reduce((n, [, r]) => n + r.length, 0);
  const facetGroups = meta?.facet_groups?.[family] || [];

  const exportMatrix = () => {
    window.open(`${API}/equipment/export?family=${family}&basis=${basis}`, "_blank");
  };

  const startProject = (t) => {
    nav(`/projects/new?equipment_type=${t.key}&industry=${t.pack_key}&basis=${basis}`);
  };

  return (
    <div className="p-6" data-testid="equipment-catalog-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl tracking-tight font-medium text-[var(--c-text)]">Equipment Type Catalog</h2>
          <p className="text-xs text-[var(--c-muted)] mt-1.5 max-w-2xl">
            Engineering taxonomy of transformer, rectifier and switchgear equipment. Classification dimensions
            (core, cooling, pulse number, insulation technology, busbar scheme…) are applied as selectable tags.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={basis} onValueChange={setBasis}>
            <SelectTrigger data-testid="ec-basis" className="w-[200px] bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]">
              <SelectValue placeholder="Standards basis" />
            </SelectTrigger>
            <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
              {(meta?.standards_bases || []).map((b) => (
                <SelectItem key={b.key} value={b.key} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" data-testid="ec-export" onClick={exportMatrix}
            className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm">
            <DownloadSimple size={14} className="mr-1.5" /> Matrix (XLSX)
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-5 border-b border-[var(--c-border)]" data-testid="ec-family-tabs">
        {(meta?.families || []).map((f) => (
          <button key={f.key} data-testid={`ec-tab-${f.key}`} onClick={() => setFamily(f.key)}
            className={`px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors duration-150 ${
              family === f.key ? "border-[var(--c-accent)] text-[var(--c-text)]" : "border-transparent text-[var(--c-muted)] hover:text-[var(--c-text)]"
            }`}>
            {f.name}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5 mt-5">
        <div className="space-y-4">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--c-muted)]" />
            <Input data-testid="ec-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search types, applications…"
              className="pl-8 bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]" />
          </div>
          <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-3 space-y-3" data-testid="ec-facets">
            <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] flex items-center gap-1.5">
              <Tag size={12} /> Classification
            </div>
            {facetGroups.map((g) => (
              <div key={g.key} className="space-y-1">
                <div className="text-[11px] text-[var(--c-muted)]">{g.label}</div>
                <select data-testid={`ec-facet-${g.key}`} value={facetFilter[g.key] || ""}
                  onChange={(e) => setFacetFilter({ ...facetFilter, [g.key]: e.target.value })}
                  className="w-full text-xs bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm px-2 py-1.5 text-[var(--c-text)]">
                  <option value="">Any</option>
                  {g.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            ))}
            {Object.values(facetFilter).some(Boolean) && (
              <button data-testid="ec-clear-facets" onClick={() => setFacetFilter({})}
                className="text-[11px] text-[var(--c-accent)] hover:underline">Clear filters</button>
            )}
          </div>
        </div>

        <div className="min-w-0">
          <div className="text-[11px] font-mono text-[var(--c-muted)] mb-2" data-testid="ec-count">
            {loading ? "Loading…" : `${shown} type${shown === 1 ? "" : "s"}`}
          </div>
          {groups.map(([cat, rows]) => (
            <div key={cat} className="mb-6">
              <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-2">{cat}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {rows.map((t) => (
                  <button key={t.key} data-testid={`ec-type-${t.key}`} onClick={() => setSel(t)}
                    className="text-left bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-3.5 hover:border-[var(--c-accent)] hover:-translate-y-0.5 transition-[border-color,transform] duration-150">
                    <div className="text-sm font-medium text-[var(--c-text)] leading-snug">{t.name}</div>
                    <div className="text-[11px] text-[var(--c-muted)] mt-1.5 line-clamp-2">{t.description}</div>
                    <div className="flex items-center gap-2 mt-2.5 text-[10px] font-mono text-[var(--c-muted)]">
                      <span>{t.rating_range || "—"}</span>
                      <span className="opacity-40">·</span>
                      <span>{t.voltage_range || "—"}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
          {!loading && shown === 0 && (
            <div className="text-sm text-[var(--c-muted)] py-10 text-center" data-testid="ec-empty">
              No equipment types match this search and filter combination.
            </div>
          )}
        </div>
      </div>

      {sel && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={() => setSel(null)} data-testid="ec-detail-overlay">
          <div className="w-full max-w-xl h-full bg-[var(--c-surface)] border-l border-[var(--c-border)] overflow-y-auto"
            onClick={(e) => e.stopPropagation()} data-testid="ec-detail">
            <div className="sticky top-0 bg-[var(--c-surface)] border-b border-[var(--c-border)] px-5 py-4 flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">{sel.category}</div>
                <h3 className="text-lg font-medium text-[var(--c-text)] leading-tight mt-0.5">{sel.name}</h3>
              </div>
              <button data-testid="ec-detail-close" onClick={() => setSel(null)}
                className="p-1.5 rounded-sm text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)]">
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-5">
              <p className="text-sm text-[var(--c-text)]/90 leading-relaxed">{sel.description}</p>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">Standards basis</span>
                <select data-testid="ec-detail-basis" value={basis} onChange={(e) => setBasis(e.target.value)}
                  className="text-xs bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm px-2 py-1 text-[var(--c-text)]">
                  {(meta?.standards_bases || []).map((b) => <option key={b.key} value={b.key}>{b.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Typical rating" value={sel.rating_range} testid="ec-d-rating" />
                <Field label="Voltage range" value={sel.voltage_range} testid="ec-d-voltage" />
              </div>
              <Block label={`Standards — ${(meta?.standards_bases || []).find((b) => b.key === basis)?.name || basis}`} testid="ec-d-standards">
                {(sel.standards?.[basis] || []).length
                  ? <ul className="space-y-1">{sel.standards[basis].map((s) => <li key={s} className="text-xs font-mono text-[var(--c-text)]">{s}</li>)}</ul>
                  : <div className="text-xs text-[var(--c-muted)]">No standard listed for this basis — switch basis above.</div>}
              </Block>
              <Block label="Cross-reference standards" testid="ec-d-other-standards">
                <div className="space-y-1.5">
                  {["iec", "ieee", "is"].filter((k) => k !== basis && (sel.standards?.[k] || []).length).map((k) => (
                    <div key={k} className="text-xs text-[var(--c-muted)]">
                      <span className="font-mono uppercase text-[var(--c-text)]">{k}</span> — {sel.standards[k].join(", ")}
                    </div>
                  ))}
                </div>
              </Block>
              <Block label="Typical applications" testid="ec-d-applications">
                <div className="flex flex-wrap gap-1.5">
                  {(sel.applications || []).map((a) => (
                    <span key={a} className="text-[11px] px-2 py-1 rounded-sm bg-[var(--c-border)]/40 text-[var(--c-text)]">{a}</span>
                  ))}
                </div>
              </Block>
              <Block label="Key design parameters" testid="ec-d-params">
                <ul className="grid grid-cols-1 gap-1">
                  {(sel.key_params || []).map((p) => (
                    <li key={p} className="text-xs text-[var(--c-text)]/85 flex gap-2"><span className="text-[var(--c-accent)]">▸</span>{p}</li>
                  ))}
                </ul>
              </Block>
              <Block label="Classification tags" testid="ec-d-facets">
                <div className="space-y-2">
                  {Object.entries(sel.facets || {}).map(([g, vals]) => (
                    <div key={g}>
                      <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--c-muted)]">
                        {(facetGroups.find((x) => x.key === g)?.label) || g}
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {vals.map((v) => (
                          <span key={v} className="text-[11px] px-2 py-0.5 rounded-sm border border-[var(--c-accent)]/40 bg-[var(--c-accent)]/10 text-[var(--c-text)]">{v}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                  {!Object.keys(sel.facets || {}).length && <div className="text-xs text-[var(--c-muted)]">No tags assigned.</div>}
                </div>
              </Block>
              <div className="flex items-center gap-2 pt-1">
                <Button data-testid="ec-start-project" onClick={() => startProject(sel)}
                  className="bg-[var(--c-accent)] hover:bg-[var(--c-accent)]/85 text-white rounded-sm">
                  <RocketLaunch size={14} className="mr-1.5" /> Create project with this type
                </Button>
                {user?.role === "admin" && (
                  <span className="text-[11px] text-[var(--c-muted)] flex items-center gap-1.5">
                    <Books size={13} /> Drives spec form: {sel.pack_key?.replace(/_/g, " ")}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, value, testid }) => (
  <div data-testid={testid}>
    <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">{label}</div>
    <div className="text-sm text-[var(--c-text)] mt-0.5">{value || "—"}</div>
  </div>
);

const Block = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-1.5">{label}</div>
    {children}
  </div>
);
